<template>
  <div
    class="rounded-[10px] border border-lightGray30 bg-[#FAFBFC]"
    :class="layout === 'side' ? 'md:sticky md:top-3' : ''"
    data-testid="send-record-attachment-panel"
  >
    <!--
      Sem anexo (tipo que dispensa arquivo, como o Ajuste administrativo) o painel
      não vira uma moldura vazia: diz o que há e sai da frente.
    -->
    <p v-if="!files.length" class="px-3 py-4 text-xs text-neutral50">Nenhum anexo neste registro</p>

    <template v-else>
      <div
        class="flex items-center justify-between gap-2 px-3 py-2"
        :class="layout === 'side' ? 'border-b border-gray-100' : ''"
      >
        <!-- `aria-live`: trocar de anexo pelo botão ou pelo dedo não muda o foco, então
             é este texto que conta ao leitor de tela qual arquivo está na tela agora. -->
        <b
          class="min-w-0 flex-1 truncate text-[13px] font-bold text-textPrimary"
          aria-live="polite"
          data-testid="send-record-attachment-name"
        >
          {{ headerLabel }}
        </b>
        <span class="flex flex-none gap-1">
          <button
            type="button"
            title="Girar"
            aria-label="Girar anexo"
            class="flex h-[26px] w-[26px] items-center justify-center rounded-md border border-lightGray30 bg-white text-neutral50"
            data-testid="send-record-attachment-rotate"
            @mousedown.prevent
            @click="rotate"
          >
            <v-icon :icon="mdiRotateRight" size="14" />
          </button>
          <button
            type="button"
            title="Ampliar"
            aria-label="Ampliar anexo"
            class="flex h-[26px] w-[26px] items-center justify-center rounded-md border border-lightGray30 bg-white text-neutral50"
            data-testid="send-record-attachment-zoom"
            @mousedown.prevent
            @click="zoomIn"
          >
            <v-icon :icon="mdiMagnifyPlusOutline" size="14" />
          </button>
          <a
            v-if="layout === 'side' && currentUrl"
            :href="currentUrl"
            target="_blank"
            rel="noopener noreferrer"
            title="Abrir em nova aba"
            aria-label="Abrir anexo em nova aba"
            class="flex h-[26px] w-[26px] items-center justify-center rounded-md border border-lightGray30 bg-white text-neutral50"
            data-testid="send-record-attachment-open"
          >
            <v-icon :icon="mdiOpenInNew" size="14" />
          </a>
        </span>
      </div>

      <!-- ------------------------------------------------------------------ -->
      <!-- Desktop: o anexo fica ao LADO do formulário, preso ao rolar (④).    -->
      <!-- ------------------------------------------------------------------ -->
      <div v-if="layout === 'side'" class="p-3">
        <StepPreview
          :file="currentFile"
          mode="preview"
          hide-toolbar
          :show-display-name="false"
          :height-px="360"
          :rotation="rotation"
          :zoom="zoom"
          @update:rotation="rotation = $event"
          @update:zoom="zoom = $event"
        />
      </div>

      <!-- ------------------------------------------------------------------ -->
      <!-- Celular: carrossel com "espiada" do próximo (⑩) e recorte ao digitar -->
      <!-- (⑫). O recorte é o centro do anexo com zoom 2x — a marcação da etapa -->
      <!-- 3 (⑬) é de outra fase e o carrossel funciona sem ela.                -->
      <!-- ------------------------------------------------------------------ -->
      <div
        v-else
        class="px-3 pb-2"
        :data-testid="
          isCompact ? 'send-record-attachment-compact' : 'send-record-attachment-carousel'
        "
      >
        <div
          ref="track"
          class="flex gap-2 overflow-x-auto snap-x snap-mandatory attachment-track"
          @scroll.passive="syncIndexWithScroll"
        >
          <div
            v-for="slide in slides"
            :key="slide.key"
            class="relative flex-none snap-center overflow-hidden rounded-lg"
            :class="isCompact ? 'basis-full' : 'basis-[88%]'"
            :role="isCompact ? undefined : 'button'"
            :tabindex="isCompact ? undefined : 0"
            :aria-label="isCompact ? undefined : `Abrir ${slide.file.name} em tela cheia`"
            @click="openFullscreen"
            @keydown.enter.prevent="openFullscreen"
            @keydown.space.prevent="openFullscreen"
          >
            <span
              v-if="isCompact"
              class="absolute left-2 top-2 z-10 rounded-full bg-[rgba(22,22,29,0.78)] px-[7px] py-0.5 text-[10.5px] font-bold tracking-wide text-white"
            >
              RECORTE · HORÁRIO
            </span>
            <StepPreview
              v-if="slide.render"
              :file="slide.file"
              mode="preview"
              hide-toolbar
              :show-display-name="false"
              :height-px="isCompact ? 96 : 170"
              :rotation="rotation"
              :zoom="isCompact ? COMPACT_ZOOM : zoom"
              @update:rotation="rotation = $event"
              @update:zoom="zoom = $event"
            />
            <!--
              Anexo longe da dobra: só a moldura, com a MESMA altura e largura. O
              carrossel continua medindo certo (o `scrollLeft` do índice depende disso)
              sem pagar um object URL e uma renderização de PDF por arquivo.
            -->
            <div
              v-else
              class="w-full bg-lightGray30/40"
              :style="{ height: `${isCompact ? 96 : 170}px` }"
              aria-hidden="true"
            />
          </div>
        </div>
      </div>

      <div
        v-if="toleranceHint"
        class="mx-3 mb-3 flex items-start gap-2 rounded-lg bg-[#E3EEF7] px-2.5 py-2 text-xs text-[#2F6F9F]"
      >
        <v-icon :icon="mdiInformationOutline" size="14" class="mt-0.5 flex-none" />
        <span>{{ toleranceHint }}</span>
      </div>

      <div
        class="flex items-center justify-between gap-2 px-3 pb-2.5 text-xs text-neutral50"
        :class="layout === 'carousel' ? 'pt-0' : ''"
      >
        <span class="inline-flex items-center gap-1.5">
          <i
            v-for="(file, position) in files"
            :key="`dot-${position}-${file.name}`"
            class="h-1.5 w-1.5 rounded-full"
            :class="position === index ? 'bg-primary30' : 'bg-lightGray30'"
          />
          {{ index + 1 }} / {{ files.length }}{{ pagerSuffix }}
        </span>

        <span class="inline-flex flex-none items-center gap-2">
          <button
            v-if="isCompact"
            type="button"
            class="font-bold text-primary30"
            data-testid="send-record-attachment-see-full"
            @mousedown.prevent
            @click="seeFull = true"
          >
            Ver inteiro
          </button>
          <template v-if="files.length > 1">
            <button
              type="button"
              class="font-bold text-primary30 disabled:opacity-40"
              :disabled="index === 0"
              data-testid="send-record-attachment-prev"
              @mousedown.prevent
              @click="go(-1)"
            >
              ‹ anterior
            </button>
            <span v-if="layout === 'side'" aria-hidden="true">·</span>
            <button
              type="button"
              class="font-bold text-primary30 disabled:opacity-40"
              :disabled="index === files.length - 1"
              data-testid="send-record-attachment-next"
              @mousedown.prevent
              @click="go(1)"
            >
              próximo ›
            </button>
          </template>
        </span>
      </div>
    </template>

    <!--
      Toque no carrossel abre o anexo em tela cheia. O visualizador embutido já
      faz zoom e arrasto; aqui ele só ganha a tela inteira.
    -->
    <teleport v-if="isFullscreen" to="body">
      <div
        class="fixed inset-0 z-[60] flex flex-col bg-black/90 p-3"
        role="dialog"
        aria-label="Anexo em tela cheia"
        data-testid="send-record-attachment-fullscreen"
      >
        <button
          type="button"
          class="self-end rounded-md bg-white px-3 py-1 text-sm font-bold text-primary30"
          data-testid="send-record-attachment-fullscreen-close"
          @click="isFullscreen = false"
        >
          Fechar
        </button>
        <div class="mt-2 flex-1 overflow-auto">
          <StepPreview
            :file="currentFile"
            mode="preview"
            :show-display-name="false"
            :rotation="rotation"
            :zoom="zoom"
            @update:rotation="rotation = $event"
            @update:zoom="zoom = $event"
          />
        </div>
      </div>
    </teleport>
  </div>
</template>

<script setup lang="ts">
import { computed, nextTick, ref, watch, onBeforeUnmount } from 'vue'
import { mdiInformationOutline, mdiMagnifyPlusOutline, mdiOpenInNew, mdiRotateRight } from '@mdi/js'
import StepPreview from '@/components/designSystem/modals/AttendanceUploadModal/StepPreview.vue'

/**
 * Painel do anexo das etapas 4 e 5 do modal "Enviar registro" (M6, balões ④ e ⑩).
 *
 * O "cara-crachá" do emissor: o arquivo enviado na etapa 3 fica à vista enquanto
 * ele declara o horário, em vez de obrigá-lo a VOLTAR uma etapa para conferir.
 * A renderização em si é do `StepPreview.vue` do wizard de folha — imagem e PDF,
 * zoom e arrasto já resolvidos —, com a barra interna escondida porque este
 * painel oferece as mesmas ferramentas no próprio cabeçalho.
 */

const props = withDefaults(
  defineProps<{
    files: File[]
    layout: 'side' | 'carousel'
    /** Celular com o teclado aberto: o carrossel encolhe para o recorte do horário. */
    compact?: boolean
    /** Dica de tolerância, no bloco azul sob o anexo. */
    toleranceHint?: string | null
    /**
     * Anexo mostrado, quando quem monta o painel também navega por ele — o "Ver"
     * da lista de arquivos da etapa 3. Ausente, a paginação é só interna.
     */
    activeIndex?: number | null
  }>(),
  { compact: false, toleranceHint: null, activeIndex: null }
)

const emit = defineEmits<{ 'update:activeIndex': [value: number] }>()

/** Zoom do recorte quando não há marcação da etapa 3: o centro do anexo, 2x (⑫). */
const COMPACT_ZOOM = 2

const index = ref(0)
const rotation = ref(0)
const zoom = ref(1)
const isFullscreen = ref(false)
const track = ref<HTMLElement | null>(null)

/**
 * "Ver inteiro" desfaz o recorte SEM fechar o teclado (os botões usam
 * `@mousedown.prevent`, então o input não perde o foco). O estado volta sozinho
 * assim que o campo é desfocado e o `compact` cai.
 */
/**
 * A rolagem suave dispara dezenas de eventos de `scroll` ANTES de chegar ao
 * slide pedido, e cada um deles ainda lê a posição antiga. Sem esta trava o
 * `syncIndexWithScroll` desfazia o clique em "próximo" no meio da animação.
 */
let programmaticScroll: ReturnType<typeof setTimeout> | null = null

const seeFull = ref(false)
const isCompact = computed(() => props.compact && !seeFull.value)

watch(
  () => props.compact,
  (compact) => {
    if (!compact) seeFull.value = false
  }
)

// Ao voltar do recorte a faixa é remontada no começo: o anexo que estava na tela
// volta para debaixo da dobra, com o zoom e a rotação que tinham sido aplicados.
watch(isCompact, (compact) => {
  if (!compact) void nextTick(scrollToIndex)
})

// Trocar de arquivo zera zoom e rotação: eles são do anexo que estava na tela.
watch(index, () => {
  rotation.value = 0
  zoom.value = 1
})

watch(
  () => props.files.length,
  (total) => {
    if (index.value > total - 1) index.value = Math.max(0, total - 1)
  }
)

watch(
  () => props.activeIndex,
  (active) => {
    if (typeof active !== 'number') return
    const clamped = Math.min(Math.max(active, 0), Math.max(0, props.files.length - 1))
    if (clamped !== index.value) {
      index.value = clamped
      void nextTick(scrollToIndex)
    }
  },
  { immediate: true }
)

// Quem controla o índice de fora precisa acompanhar a paginação feita aqui dentro.
// Sem `activeIndex` o painel é autônomo, e um evento que ninguém pediu só faria ruído.
watch(index, (value) => {
  if (props.activeIndex !== null) emit('update:activeIndex', value)
})

const currentFile = computed<File | null>(() => props.files[index.value] ?? null)

/**
 * No recorte só o anexo atual entra na faixa — o "espiar o próximo" é do modo
 * inteiro. E só o slide visível e os vizinhos imediatos renderizam de verdade:
 * dez anexos na faixa seriam dez visualizadores vivos ao mesmo tempo.
 */
const slides = computed(() =>
  (isCompact.value ? [currentFile.value] : props.files)
    .filter((file): file is File => file !== null)
    .map((file, position) => ({
      key: `${position}-${file.name}`,
      file,
      render: isCompact.value || Math.abs(position - index.value) <= 1
    }))
)

const headerLabel = computed(() => {
  const total = props.files.length
  const name = isCompact.value ? 'recorte do horário' : (currentFile.value?.name ?? '')
  return `Anexo ${index.value + 1} de ${total}${name ? ` · ${name}` : ''}`
})

const pagerSuffix = computed(() =>
  props.layout === 'carousel' && !isCompact.value && props.files.length > 1
    ? ' · deslize para o próximo'
    : ''
)

/**
 * Object URL só do anexo aberto e só onde ele é usado: o "abrir em nova aba"
 * existe apenas no painel de desktop. No carrossel o `StepPreview` já cria (e
 * revoga) o dele, e um segundo URL por anexo seria memória presa à toa.
 */
const currentUrl = ref<string | null>(null)

function releaseUrl(): void {
  if (currentUrl.value) URL.revokeObjectURL(currentUrl.value)
  currentUrl.value = null
}

watch(
  [currentFile, () => props.layout],
  ([file, layout]) => {
    releaseUrl()
    if (layout !== 'side') return
    if (file && typeof URL.createObjectURL === 'function') {
      currentUrl.value = URL.createObjectURL(file)
    }
  },
  { immediate: true }
)

onBeforeUnmount(() => {
  releaseUrl()
  if (programmaticScroll) clearTimeout(programmaticScroll)
})

function rotate(): void {
  rotation.value = (rotation.value + 90) % 360
}

/** Um botão só: "Ampliar" cicla 1x → 1,5x → … → 4x e volta ao tamanho natural. */
function zoomIn(): void {
  zoom.value = zoom.value >= 4 ? 1 : Math.round((zoom.value + 0.5) * 10) / 10
}

function go(step: number): void {
  const next = index.value + step
  if (next < 0 || next > props.files.length - 1) return
  index.value = next
  scrollToIndex()
}

function scrollToIndex(): void {
  const element = track.value
  const slide = element?.firstElementChild as HTMLElement | null
  if (!element || !slide || !slide.offsetWidth || typeof element.scrollTo !== 'function') return
  if (programmaticScroll) clearTimeout(programmaticScroll)
  programmaticScroll = setTimeout(() => {
    programmaticScroll = null
  }, 400)
  element.scrollTo({ left: index.value * (slide.offsetWidth + 8), behavior: 'smooth' })
}

/** O dedo manda: o índice segue o slide que parou sob a dobra da faixa. */
function syncIndexWithScroll(): void {
  // No recorte a faixa tem UM slide e o `scrollLeft` é sempre 0: ler a posição
  // aqui jogaria o índice de volta ao primeiro anexo assim que o teclado abrisse.
  if (isCompact.value || programmaticScroll) return
  const element = track.value
  const slide = element?.firstElementChild as HTMLElement | null
  if (!element || !slide || !slide.offsetWidth) return
  const position = Math.round(element.scrollLeft / (slide.offsetWidth + 8))
  const clamped = Math.min(Math.max(position, 0), props.files.length - 1)
  if (clamped !== index.value) index.value = clamped
}

function openFullscreen(): void {
  if (props.layout !== 'carousel' || isCompact.value) return
  isFullscreen.value = true
}
</script>

<style scoped>
/* A barra de rolagem da faixa some: quem navega no celular usa o dedo e os pontos. */
.attachment-track {
  scrollbar-width: none;
  -webkit-overflow-scrolling: touch;
}
.attachment-track::-webkit-scrollbar {
  display: none;
}
</style>
